import { useState, useEffect, type ReactNode } from 'react';
import { useAuthStore } from '../stores/auth';
import { api } from '../utils/api-client';

interface OnboardingFlowProps {
  onComplete: () => void;
}

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  component: ReactNode;
}

export function OnboardingFlow({ onComplete }: OnboardingFlowProps): ReactNode {
  useAuthStore(); // Ensure auth context is available
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if user has completed onboarding
  useEffect(() => {
    void checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async (): Promise<void> => {
    try {
      // Check if user has links or content (indicates they've started)
      const [linksRes, contentRes] = await Promise.allSettled([
        api.get<{ links?: unknown[] }>('/affiliate/links').catch(() => ({ links: [] })),
        api.get<{ content?: unknown[] }>('/content').catch(() => ({ content: [] })),
      ]);

      const hasLinks = linksRes.status === 'fulfilled' && linksRes.value && 
        'links' in linksRes.value && 
        Array.isArray((linksRes.value as { links?: unknown[] }).links) && 
        (linksRes.value as { links: unknown[] }).links.length > 0;
      const hasContent = contentRes.status === 'fulfilled' && contentRes.value && 
        'content' in contentRes.value && 
        Array.isArray((contentRes.value as { content?: unknown[] }).content) && 
        (contentRes.value as { content: unknown[] }).content.length > 0;

      if (hasLinks || hasContent) {
        setIsDismissed(true);
        onComplete();
      }
    } catch {
      // Ignore errors, show onboarding anyway
    }
  };

  const steps: OnboardingStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to ProfitOS!',
      description: "Let's get you set up in 3 quick steps",
      component: <WelcomeStep onNext={() => handleStepComplete('welcome')} />,
    },
    {
      id: 'create-link',
      title: 'Create Your First Link',
      description: 'Start tracking your affiliate performance',
      component: <CreateLinkStep onNext={() => handleStepComplete('create-link')} />,
    },
    {
      id: 'explore-offers',
      title: 'Explore the Marketplace',
      description: 'Discover high-converting affiliate offers',
      component: <ExploreOffersStep onNext={() => handleStepComplete('explore-offers')} />,
    },
    {
      id: 'complete',
      title: "You're All Set!",
      description: 'Start building your affiliate business',
      component: <CompleteStep onComplete={onComplete} />,
    },
  ];

  const handleStepComplete = (stepId: string): void => {
    setCompletedSteps(new Set([...completedSteps, stepId]));
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleDismiss = (): void => {
    setIsDismissed(true);
    onComplete();
  };

  if (isDismissed) return null;

  const step = steps[currentStep];
  if (!step) return null;
  
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-2xl rounded-lg bg-white shadow-xl">
        {/* Progress Bar */}
        <div className="h-1 bg-gray-200">
          <div
            className="h-full bg-primary-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Header */}
        <div className="border-b p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{step?.title ?? ''}</h2>
              <p className="mt-1 text-sm text-gray-500">{step?.description ?? ''}</p>
            </div>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="mt-4 flex items-center gap-2">
            {steps.map((s, i) => (
              <div
                key={s.id}
                className={`h-2 flex-1 rounded-full ${
                  i <= currentStep ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">{step?.component}</div>

        {/* Footer */}
        <div className="border-t bg-gray-50 p-6">
          <div className="flex justify-between">
            {currentStep > 0 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-white"
              >
                Previous
              </button>
            )}
            <div className="ml-auto flex gap-3">
              <button
                onClick={handleDismiss}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-white"
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WelcomeStep({ onNext }: { onNext: () => void }): ReactNode {
  return (
    <div className="space-y-6 text-center">
      <div className="mx-auto h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
        <svg className="h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Welcome to ProfitOS!</h3>
        <p className="mt-2 text-gray-600">
          Your AI-powered affiliate operating system. We'll help you set up in just a few minutes.
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border p-4">
          <div className="text-2xl font-bold text-primary-600">1</div>
          <div className="mt-2 text-sm font-medium text-gray-900">Create Links</div>
          <div className="mt-1 text-xs text-gray-500">Track your affiliate performance</div>
        </div>
        <div className="rounded-lg border p-4">
          <div className="text-2xl font-bold text-primary-600">2</div>
          <div className="mt-2 text-sm font-medium text-gray-900">Find Offers</div>
          <div className="mt-1 text-xs text-gray-500">Discover high-converting products</div>
        </div>
        <div className="rounded-lg border p-4">
          <div className="text-2xl font-bold text-primary-600">3</div>
          <div className="mt-2 text-sm font-medium text-gray-900">Generate Content</div>
          <div className="mt-1 text-xs text-gray-500">AI-powered content creation</div>
        </div>
      </div>
      <button
        onClick={onNext}
        className="w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
      >
        Get Started
      </button>
    </div>
  );
}

function CreateLinkStep({ onNext }: { onNext: () => void }): ReactNode {
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
        <div className="flex items-start">
          <svg className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-primary-900">Quick Tip</h4>
            <p className="mt-1 text-sm text-primary-700">
              Create your first affiliate link to start tracking clicks and conversions. You can always add more later!
            </p>
          </div>
        </div>
      </div>
      <div className="text-center">
        <a
          href="/links"
          className="inline-flex items-center rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-white hover:bg-primary-700"
        >
          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Your First Link
        </a>
      </div>
      <button
        onClick={onNext}
        className="w-full rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        I'll do this later
      </button>
    </div>
  );
}

function ExploreOffersStep({ onNext }: { onNext: () => void }): ReactNode {
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
        <div className="flex items-start">
          <svg className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-primary-900">Discover Offers</h4>
            <p className="mt-1 text-sm text-primary-700">
              Browse our marketplace to find high-converting affiliate offers. Our AI scores each offer based on profitability, trending status, and competition.
            </p>
          </div>
        </div>
      </div>
      <div className="text-center">
        <a
          href="/marketplace"
          className="inline-flex items-center rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-white hover:bg-primary-700"
        >
          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
          Explore Marketplace
        </a>
      </div>
      <button
        onClick={onNext}
        className="w-full rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        Continue
      </button>
    </div>
  );
}

function CompleteStep({ onComplete }: { onComplete: () => void }): ReactNode {
  return (
    <div className="space-y-6 text-center">
      <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
        <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900">You're All Set!</h3>
        <p className="mt-2 text-gray-600">
          You're ready to start building your affiliate business. Need help? Check out our documentation or contact support.
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <a
          href="/links"
          className="rounded-lg border p-4 hover:border-primary-500 hover:bg-primary-50 transition-colors"
        >
          <div className="text-sm font-medium text-gray-900">Create Links</div>
          <div className="mt-1 text-xs text-gray-500">Start tracking performance</div>
        </a>
        <a
          href="/marketplace"
          className="rounded-lg border p-4 hover:border-primary-500 hover:bg-primary-50 transition-colors"
        >
          <div className="text-sm font-medium text-gray-900">Browse Offers</div>
          <div className="mt-1 text-xs text-gray-500">Find high-converting products</div>
        </a>
      </div>
      <button
        onClick={onComplete}
        className="w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
      >
        Go to Dashboard
      </button>
    </div>
  );
}
